import { haveSearchParamsChanged, parseRetrievedState } from '@sky-distribution/gp-onboarding-shared-utils';
import { useInterpret } from '@xstate/react';
import { merge } from 'lodash';
import {
  FC,
  PropsWithChildren,
  createContext,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { InterpreterFrom, State, createMachine } from 'xstate';
import { getDefaultMachineConfig, getDefaultMachineOptions } from '../const';
import { AppStateMachine, AppStateMachineContext } from '../types';
import { AppStateProviderProps } from './AppStateProvider.types';

export const AppStateContext = createContext(
  {} as InterpreterFrom<AppStateMachine>,
);

export const AppStateProvider: FC<PropsWithChildren<AppStateProviderProps>> = ({
  children,
  productId,
  interpreterOptions,
  machineConfig,
  machineOptions,
  pathname,
  getContextConfiguration,
  restartOnSearchParams,
}) => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;

  const contextConfiguration = useMemo(
    () => getContextConfiguration(tRef.current),
    [getContextConfiguration],
  );

  const machine = useMemo(() => {
    const defaultMachineConfig = getDefaultMachineConfig(productId, {
      ...contextConfiguration, route: pathname,
    } as AppStateMachineContext);

    const defaultMachineOptions = getDefaultMachineOptions(
      pathname,
      defaultMachineConfig.context as AppStateMachineContext,
    );

    return createMachine(
      merge(machineConfig, defaultMachineConfig),
      merge(machineOptions, defaultMachineOptions),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, pathname]);

  const stateDefinition = parseRetrievedState<typeof machine.initialState>();
  const isSameProduct = stateDefinition?.context.productCode === contextConfiguration.productCode;
  const isRestartedBySearchParams = haveSearchParamsChanged(
    restartOnSearchParams,
    stateDefinition?.context.searchParams,
  );
  const canRestoreState = isSameProduct && !!stateDefinition && !isRestartedBySearchParams;
  const previousState = canRestoreState ? State.create(stateDefinition) : machine.initialState;

  const stateService = useInterpret(machine, interpreterOptions)
    .start(previousState);

  return (
    <AppStateContext.Provider value={stateService}>
      {children}
    </AppStateContext.Provider>
  );
};
